import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import moment from 'moment';
import { find, reduce, filter } from 'lodash';
import { runInAction } from 'mobx';
import InvestigationStore from '@/store/Investigation/InvestigationStore';
import StationStore from '@/store/Station/StationStore';
import { EXPANDABLE_SECTION_KEYS } from '@/constants/investigationExpandableSections';
import { ComponentTierName } from '@/constants/enums';
import { checkTierPermission } from '@/utils/util-functions';
import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';

const getVisibleDevices = (
  investigationStore: InvestigationStore,
  stationStore: StationStore,
): Array<{ id: string; deviceTypes?: string[] }> => {
  if (!investigationStore.sortedDevicesMetrics) {
    return [];
  }

  const shouldRestrictInvestigationDashboard = checkTierPermission(
    ComponentTierName.InvestigationDashboard,
    stationStore,
  );

  const isCompressor = (deviceTypes: string[] = []) => {
    return !!find(deviceTypes, type => compressorTypes.includes(type as DeviceType));
  };

  return investigationStore.sortedDevicesMetrics.filter(device =>
    shouldRestrictInvestigationDashboard ? isCompressor(device.deviceTypes || []) : true,
  );
};

export const parseDatesFromParams = (
  searchParams: URLSearchParams,
  startKey: string,
  endKey: string,
): [moment.Moment, moment.Moment] | null => {
  const startTimestamp = searchParams.get(startKey);
  const endTimestamp = searchParams.get(endKey);

  if (startTimestamp && endTimestamp) {
    const startDate = moment(Number(startTimestamp));
    const endDate = moment(Number(endTimestamp));

    if (startDate.isValid() && endDate.isValid()) {
      return [startDate, endDate];
    }
  }

  return null;
};

const doStoreDatesMatchUrl = (investigationStore: InvestigationStore, searchParams: URLSearchParams): boolean => {
  const urlDates1 = parseDatesFromParams(searchParams, 'timestamp1Start', 'timestamp1End');
  const urlDates2 = parseDatesFromParams(searchParams, 'timestamp2Start', 'timestamp2End');

  const storeDates1 = investigationStore.dates1;
  const storeDates2 = investigationStore.dates2;
  const storeIsCompareMode = investigationStore.isCompareMode;

  if (!urlDates1 && !storeDates1) {
    return true;
  }

  if (!urlDates1 || !storeDates1) {
    return false;
  }

  const dates1Match =
    storeDates1[0]?.valueOf() === urlDates1[0]?.valueOf() && storeDates1[1]?.valueOf() === urlDates1[1]?.valueOf();

  if (!dates1Match) {
    return false;
  }

  if (urlDates2 && storeIsCompareMode && storeDates2) {
    return (
      storeDates2[0]?.valueOf() === urlDates2[0]?.valueOf() && storeDates2[1]?.valueOf() === urlDates2[1]?.valueOf()
    );
  }

  if (!urlDates2 && !storeIsCompareMode) {
    return true;
  }

  return false;
};

export const syncDatesFromUrl = (investigationStore: InvestigationStore, searchParams: URLSearchParams): void => {
  if (!investigationStore.isReady) {
    return;
  }

  if (doStoreDatesMatchUrl(investigationStore, searchParams)) {
    return;
  }

  const dates1 = parseDatesFromParams(searchParams, 'timestamp1Start', 'timestamp1End');

  if (dates1) {
    investigationStore.setDates1(dates1);

    const dates2 = parseDatesFromParams(searchParams, 'timestamp2Start', 'timestamp2End');
    if (dates2) {
      investigationStore.setIsCompareMode(true);
      investigationStore.setDates2(dates2);
    } else {
      investigationStore.setIsCompareMode(false);
      investigationStore.setDates2(null);
    }
  } else {
    const hasDateParams = searchParams.get('timestamp1Start') || searchParams.get('timestamp2Start');
    if (!hasDateParams && investigationStore.dates1) {
      investigationStore.setDates1(null);
      investigationStore.setDates2(null);
      investigationStore.setIsCompareMode(false);
    }
  }
};

export const syncMetricsFromUrl = (
  investigationStore: InvestigationStore,
  metricIdsFromUrl: string | null,
): { devicesToExpand: string[]; expandableSectionsToExpand: string[]; allMetricsSynced: boolean } => {
  const devicesToExpand: string[] = [];
  const expandableSectionsToExpand: string[] = [];

  if (!metricIdsFromUrl || !investigationStore.isReady) {
    return { devicesToExpand, expandableSectionsToExpand, allMetricsSynced: true };
  }

  const metricIds = metricIdsFromUrl
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);

  investigationStore.devicesMetrics.forEach(device => {
    device.metrics.forEach(metric => {
      metric.isSelected = false;
    });
  });
  investigationStore.stationMetrics.forEach(metric => {
    metric.isSelected = false;
  });
  investigationStore.pressureDrops.forEach(pressureDrop => {
    pressureDrop.isSelected = false;
  });

  const needsDeviceMetrics = metricIds.some(
    id =>
      !id.startsWith(`${EXPANDABLE_SECTION_KEYS.STATION_KPI}:`) &&
      !id.startsWith(`${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:`),
  );
  const needsStationMetrics = metricIds.some(id => id.startsWith(`${EXPANDABLE_SECTION_KEYS.STATION_KPI}:`));
  const needsPressureDrops = metricIds.some(id => id.startsWith(`${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:`));

  const dates1Valid =
    investigationStore.dates1 &&
    Array.isArray(investigationStore.dates1) &&
    investigationStore.dates1.length === 2 &&
    investigationStore.dates1[0] &&
    investigationStore.dates1[1];

  if (!dates1Valid) {
    return { devicesToExpand, expandableSectionsToExpand, allMetricsSynced: false };
  }

  if (
    (needsStationMetrics &&
      (!Array.isArray(investigationStore.stationMetrics) || investigationStore.stationMetrics.length === 0)) ||
    (needsPressureDrops &&
      (!Array.isArray(investigationStore.pressureDrops) || investigationStore.pressureDrops.length === 0))
  ) {
    return { devicesToExpand, expandableSectionsToExpand, allMetricsSynced: false };
  }

  const selectedMetrics: string[] = [];
  const notFoundMetrics: string[] = [];
  const metricsToSelect: Array<{ type: 'device' | 'station' | 'pressureDrop'; id: string }> = [];

  for (const stableMetricId of metricIds) {
    if (stableMetricId.startsWith(`${EXPANDABLE_SECTION_KEYS.STATION_KPI}:`)) {
      const metricName = stableMetricId.replace(`${EXPANDABLE_SECTION_KEYS.STATION_KPI}:`, '');
      const metric = find(investigationStore.stationMetrics, { key: metricName });
      if (metric) {
        metricsToSelect.push({ type: 'station', id: metric.id });
        selectedMetrics.push(stableMetricId);
        if (!expandableSectionsToExpand.includes('station-total')) {
          expandableSectionsToExpand.push('station-total');
        }
      } else {
        notFoundMetrics.push(stableMetricId);
      }
    } else if (stableMetricId.startsWith(`${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:`)) {
      const key = stableMetricId.replace(`${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:`, '');
      const pressureDrop = find(investigationStore.pressureDrops, { key });
      if (pressureDrop) {
        metricsToSelect.push({ type: 'pressureDrop', id: pressureDrop.id });
        selectedMetrics.push(stableMetricId);
        if (!expandableSectionsToExpand.includes('pressure-drop')) {
          expandableSectionsToExpand.push('pressure-drop');
        }
      } else {
        notFoundMetrics.push(stableMetricId);
      }
    } else {
      const [deviceId, metricName] = stableMetricId.split(':');
      if (deviceId && metricName) {
        if (!Array.isArray(investigationStore.devicesMetrics) || investigationStore.devicesMetrics.length === 0) {
          notFoundMetrics.push(stableMetricId);
          continue;
        }

        const deviceExists = investigationStore.devicesMetrics.some(device => device.id === deviceId);
        if (!deviceExists) {
          notFoundMetrics.push(stableMetricId);
          continue;
        }

        const allDeviceMetrics = reduce(
          investigationStore.devicesMetrics,
          (result, value) => [...result, ...value.metrics],
          [],
        );
        const metric = find(allDeviceMetrics, { deviceId, name: metricName });
        if (metric) {
          metricsToSelect.push({ type: 'device', id: metric.id });
          selectedMetrics.push(stableMetricId);
          if (!devicesToExpand.includes(deviceId)) {
            devicesToExpand.push(deviceId);
          }
        } else {
          notFoundMetrics.push(stableMetricId);
        }
      }
    }
  }

  if (metricsToSelect.length > 0) {
    runInAction(() => {
      metricsToSelect.forEach(({ type, id }) => {
        if (type === 'device') {
          investigationStore.setSelectedMetric(id, true);
        } else if (type === 'station') {
          investigationStore.setSelectedStationMetric(id, true);
        } else if (type === 'pressureDrop') {
          investigationStore.setSelectedPressureDrop(id, true);
        }
      });
    });

    const selectedAfterSync = getSelectedMetricIdsFromStore(investigationStore);
    const allRequestedMetricsSelected = selectedMetrics.every(metricId => selectedAfterSync.includes(metricId));

    if (!allRequestedMetricsSelected) {
      const allMetricsSynced = false;
      return { devicesToExpand, expandableSectionsToExpand, allMetricsSynced };
    }
  }

  const hasUnsyncedDeviceMetrics =
    needsDeviceMetrics &&
    notFoundMetrics.some(
      id =>
        !id.startsWith(`${EXPANDABLE_SECTION_KEYS.STATION_KPI}:`) &&
        !id.startsWith(`${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:`),
    );
  const allMetricsSynced = notFoundMetrics.length === 0 || !hasUnsyncedDeviceMetrics;

  return { devicesToExpand, expandableSectionsToExpand, allMetricsSynced };
};

const getSelectedMetricIdsFromStore = (investigationStore: InvestigationStore): string[] => {
  const metricIds: string[] = [];

  const selectedDeviceMetrics = reduce(
    investigationStore.devicesMetrics || [],
    (result, device) => [...result, ...(device.metrics || [])],
    [],
  ).filter(metric => metric.isSelected);

  selectedDeviceMetrics.forEach(metric => {
    const stableMetricId = `${metric.deviceId}:${metric.name}`;
    metricIds.push(stableMetricId);
  });

  const selectedStationMetrics = filter(investigationStore.stationMetrics || [], { isSelected: true });
  selectedStationMetrics.forEach(metric => {
    const stableMetricId = `${EXPANDABLE_SECTION_KEYS.STATION_KPI}:${metric.key}`;
    metricIds.push(stableMetricId);
  });

  const selectedPressureDrops = filter(investigationStore.pressureDrops || [], { isSelected: true });
  selectedPressureDrops.forEach(pressureDrop => {
    const stableMetricId = `${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:${pressureDrop.key}`;
    metricIds.push(stableMetricId);
  });

  return metricIds.sort();
};

const getExpandStateFromStore = (
  investigationStore: InvestigationStore,
): { devicesToExpand: string[]; expandableSectionsToExpand: string[] } => {
  const devicesToExpand: string[] = [];
  const expandableSectionsToExpand: string[] = [];

  const selectedDeviceMetrics = reduce(
    investigationStore.devicesMetrics || [],
    (result, device) => [...result, ...(device.metrics || [])],
    [],
  ).filter(metric => metric.isSelected);

  selectedDeviceMetrics.forEach(metric => {
    if (!devicesToExpand.includes(metric.deviceId)) {
      devicesToExpand.push(metric.deviceId);
    }
  });

  const selectedStationMetrics = filter(investigationStore.stationMetrics || [], { isSelected: true });
  if (selectedStationMetrics.length > 0 && !expandableSectionsToExpand.includes('station-total')) {
    expandableSectionsToExpand.push('station-total');
  }

  const selectedPressureDrops = filter(investigationStore.pressureDrops || [], { isSelected: true });
  if (selectedPressureDrops.length > 0 && !expandableSectionsToExpand.includes('pressure-drop')) {
    expandableSectionsToExpand.push('pressure-drop');
  }

  return { devicesToExpand, expandableSectionsToExpand };
};

const doStoreMetricsMatchUrl = (investigationStore: InvestigationStore, metricIdsFromUrl: string | null): boolean => {
  if (!metricIdsFromUrl) {
    const storeMetricIds = getSelectedMetricIdsFromStore(investigationStore);
    return storeMetricIds.length === 0;
  }

  const urlMetricIds = metricIdsFromUrl
    .split(',')
    .map(id => id.trim())
    .filter(Boolean)
    .sort();

  const storeMetricIds = getSelectedMetricIdsFromStore(investigationStore);

  if (urlMetricIds.length !== storeMetricIds.length) {
    return false;
  }

  return urlMetricIds.every((id, index) => id === storeMetricIds[index]);
};

const hasExpandStateChanged = (
  newState: { devicesToExpand: string[]; expandableSectionsToExpand: string[] },
  currentDevices: string[],
  currentSections: string[],
): boolean => {
  return (
    newState.devicesToExpand.length !== currentDevices.length ||
    newState.expandableSectionsToExpand.length !== currentSections.length ||
    newState.devicesToExpand.some(id => !currentDevices.includes(id)) ||
    newState.expandableSectionsToExpand.some(id => !currentSections.includes(id))
  );
};

const updateExpandStateIfChanged = (
  newState: { devicesToExpand: string[]; expandableSectionsToExpand: string[] },
  currentDevices: string[],
  currentSections: string[],
  setDevicesToExpand: (devices: string[]) => void,
  setExpandableSectionsToExpand: (sections: string[]) => void,
): void => {
  if (hasExpandStateChanged(newState, currentDevices, currentSections)) {
    setDevicesToExpand(newState.devicesToExpand);
    setExpandableSectionsToExpand(newState.expandableSectionsToExpand);
  }
};

const syncFromUrlToStore = (
  investigationStore: InvestigationStore,
  searchParams: URLSearchParams,
): { devicesToExpand: string[]; expandableSectionsToExpand: string[]; allMetricsSynced: boolean } => {
  syncDatesFromUrl(investigationStore, searchParams);
  const metricIdsFromUrl = searchParams.get('metricId');

  if (doStoreMetricsMatchUrl(investigationStore, metricIdsFromUrl)) {
    const devicesToExpand: string[] = [];
    const expandableSectionsToExpand: string[] = [];

    if (metricIdsFromUrl) {
      const metricIds = metricIdsFromUrl
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);

      metricIds.forEach(stableMetricId => {
        if (stableMetricId.startsWith(`${EXPANDABLE_SECTION_KEYS.STATION_KPI}:`)) {
          if (!expandableSectionsToExpand.includes('station-total')) {
            expandableSectionsToExpand.push('station-total');
          }
        } else if (stableMetricId.startsWith(`${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:`)) {
          if (!expandableSectionsToExpand.includes('pressure-drop')) {
            expandableSectionsToExpand.push('pressure-drop');
          }
        } else {
          const [deviceId] = stableMetricId.split(':');
          if (deviceId && !devicesToExpand.includes(deviceId)) {
            devicesToExpand.push(deviceId);
          }
        }
      });
    }

    return { devicesToExpand, expandableSectionsToExpand, allMetricsSynced: true };
  }

  return syncMetricsFromUrl(investigationStore, metricIdsFromUrl);
};

const syncFromStoreToUrl = (
  investigationStore: InvestigationStore,
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams, options?: { replace?: boolean }) => void,
): void => {
  const newParams = new URLSearchParams(searchParams);

  const dates1 = investigationStore.dates1;
  const dates2 = investigationStore.isCompareMode ? investigationStore.dates2 : null;

  if (dates1 && dates1[0] && dates1[1]) {
    newParams.set('timestamp1Start', dates1[0].valueOf().toString());
    newParams.set('timestamp1End', dates1[1].valueOf().toString());
  } else {
    newParams.delete('timestamp1Start');
    newParams.delete('timestamp1End');
  }

  if (dates2 && dates2[0] && dates2[1]) {
    newParams.set('timestamp2Start', dates2[0].valueOf().toString());
    newParams.set('timestamp2End', dates2[1].valueOf().toString());
  } else {
    newParams.delete('timestamp2Start');
    newParams.delete('timestamp2End');
  }

  const selectedDeviceMetrics = reduce(
    investigationStore.devicesMetrics || [],
    (result, device) => [...result, ...(device.metrics || [])],
    [],
  ).filter(metric => metric.isSelected);

  const selectedStationMetrics = filter(investigationStore.stationMetrics || [], { isSelected: true });
  const selectedPressureDrops = filter(investigationStore.pressureDrops || [], { isSelected: true });

  const metricIds: string[] = [];

  selectedDeviceMetrics.forEach(metric => {
    const stableMetricId = `${metric.deviceId}:${metric.name}`;
    metricIds.push(stableMetricId);
  });

  selectedStationMetrics.forEach(metric => {
    const stableMetricId = `${EXPANDABLE_SECTION_KEYS.STATION_KPI}:${metric.key}`;
    metricIds.push(stableMetricId);
  });

  selectedPressureDrops.forEach(pressureDrop => {
    const stableMetricId = `${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:${pressureDrop.key}`;
    metricIds.push(stableMetricId);
  });

  if (metricIds.length > 0) {
    newParams.set('metricId', metricIds.join(','));
  } else {
    newParams.delete('metricId');
  }

  setSearchParams(newParams, { replace: true });
};

export const useInvestigationSync = (investigationStore: InvestigationStore) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const stationStore = investigationStore.rootStore?.stationStore;

  const hasSyncedRef = useRef(false);
  const lastStationIdRef = useRef<string | null>(null);
  const lastSyncedParamsRef = useRef<string>('');
  const previousPathnameRef = useRef<string | null>(null);
  const justEnteredTabRef = useRef<boolean>(false);
  const hasSyncedStoreToUrlRef = useRef<boolean>(false);
  const initialExpandCompleteRef = useRef<boolean>(false);
  const lastMetricsLengthRef = useRef<number>(0);
  const lastDevicesMetricsLengthRef = useRef<number>(0);
  const lastStationMetricsLengthRef = useRef<number>(0);
  const isSyncingRef = useRef<boolean>(false);
  const [devicesToExpand, setDevicesToExpand] = useState<string[]>([]);
  const [expandableSectionsToExpand, setExpandableSectionsToExpand] = useState<string[]>([]);

  useEffect(() => {
    const isInvestigationTab = location.pathname.includes('/tab/investigation');
    const hasDateParams = searchParams.get('timestamp1Start') || searchParams.get('timestamp2Start');
    const hasMetricParams = searchParams.get('metricId');
    const fromOtherPage = searchParams.get('fromOtherPage') === 'true';

    if (isInvestigationTab && fromOtherPage && (hasDateParams || hasMetricParams)) {
      const datesFromUrl = parseDatesFromParams(searchParams, 'timestamp1Start', 'timestamp1End');
      if (datesFromUrl) {
        investigationStore.setDates1(datesFromUrl);
      }
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('fromOtherPage');
      setSearchParams(newParams, { replace: true });
      isSyncingRef.current = false;
      return;
    }

    if (isSyncingRef.current) {
      return;
    }

    isSyncingRef.current = true;

    const wasInvestigationTab = previousPathnameRef.current?.includes('/tab/investigation') || false;
    const justEnteredTab = !wasInvestigationTab && isInvestigationTab;
    const justLeftTab = wasInvestigationTab && !isInvestigationTab;
    const currentStationId = stationStore?.currentStation?.id;
    const currentParamsString = searchParams.toString();
    const metricIdsFromUrl = searchParams.get('metricId');

    previousPathnameRef.current = location.pathname;
    if (justLeftTab) {
      hasSyncedRef.current = false;
      justEnteredTabRef.current = false;
      hasSyncedStoreToUrlRef.current = false;
      initialExpandCompleteRef.current = false;
      lastMetricsLengthRef.current = 0;
      lastDevicesMetricsLengthRef.current = 0;
      lastStationMetricsLengthRef.current = 0;
    }

    if (!isInvestigationTab) {
      isSyncingRef.current = false;
      return;
    }

    if (lastStationIdRef.current !== null && lastStationIdRef.current !== currentStationId) {
      hasSyncedRef.current = false;
      lastSyncedParamsRef.current = '';
      lastStationIdRef.current = currentStationId;
      justEnteredTabRef.current = false;
      hasSyncedStoreToUrlRef.current = false;
      initialExpandCompleteRef.current = false;
      lastMetricsLengthRef.current = 0;
      lastDevicesMetricsLengthRef.current = 0;
      lastStationMetricsLengthRef.current = 0;
      setDevicesToExpand([]);
      setExpandableSectionsToExpand([]);
      isSyncingRef.current = false;
      return;
    }

    if (lastStationIdRef.current === null) {
      lastStationIdRef.current = currentStationId;
    }

    if (!investigationStore.isReady) {
      isSyncingRef.current = false;
      return;
    }

    const devicesStoreReady = investigationStore.rootStore?.devicesStore?.isReady;
    if (!devicesStoreReady) {
      isSyncingRef.current = false;
      return;
    }

    const needsDeviceMetricsCheck = metricIdsFromUrl
      ?.split(',')
      .some(
        id =>
          !id.startsWith(`${EXPANDABLE_SECTION_KEYS.STATION_KPI}:`) &&
          !id.startsWith(`${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:`),
      );
    const needsStationMetricsCheck = metricIdsFromUrl
      ?.split(',')
      .some(id => id.startsWith(`${EXPANDABLE_SECTION_KEYS.STATION_KPI}:`));
    const needsPressureDropsCheck = metricIdsFromUrl
      ?.split(',')
      .some(id => id.startsWith(`${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:`));

    // Check if all required metric arrays are populated
    const devicesMetricsReady = !needsDeviceMetricsCheck || (investigationStore.devicesMetrics?.length || 0) > 0;
    const stationMetricsReady = !needsStationMetricsCheck || (investigationStore.stationMetrics?.length || 0) > 0;
    const pressureDropsReady = !needsPressureDropsCheck || (investigationStore.pressureDrops?.length || 0) > 0;

    const allMetricsReady = devicesMetricsReady && stationMetricsReady && pressureDropsReady;

    if (!allMetricsReady) {
      isSyncingRef.current = false;
      return;
    }
    const storeHasDates = !!investigationStore.dates1;
    const storeHasSelectedMetrics =
      investigationStore.devicesMetrics?.some(d => d.metrics?.some(m => m.isSelected)) ||
      investigationStore.stationMetrics?.some(m => m.isSelected) ||
      investigationStore.pressureDrops?.some(p => p.isSelected);

    if (
      justEnteredTab &&
      !hasDateParams &&
      !hasMetricParams &&
      (storeHasDates || storeHasSelectedMetrics) &&
      !hasSyncedStoreToUrlRef.current
    ) {
      syncFromStoreToUrl(investigationStore, searchParams, setSearchParams);
      if (!initialExpandCompleteRef.current) {
        const { devicesToExpand: devices, expandableSectionsToExpand: sections } =
          getExpandStateFromStore(investigationStore);
        setDevicesToExpand(devices);
        setExpandableSectionsToExpand(sections);
        initialExpandCompleteRef.current = true;
      }
      justEnteredTabRef.current = true;
      hasSyncedStoreToUrlRef.current = true;
      lastStationIdRef.current = currentStationId;
      isSyncingRef.current = false;
      return;
    }

    if (justEnteredTabRef.current) {
      const stillMissingDates = storeHasDates && !hasDateParams;
      const stillMissingMetrics = storeHasSelectedMetrics && !hasMetricParams;

      if (stillMissingDates || stillMissingMetrics) {
        isSyncingRef.current = false;
        return;
      }

      const allMissingParamsNowPresent =
        (!storeHasDates || hasDateParams) && (!storeHasSelectedMetrics || hasMetricParams);

      if (allMissingParamsNowPresent) {
        const {
          devicesToExpand: devices,
          expandableSectionsToExpand: sections,
          allMetricsSynced,
        } = syncFromUrlToStore(investigationStore, searchParams);

        if (!initialExpandCompleteRef.current) {
          setDevicesToExpand(devices);
          setExpandableSectionsToExpand(sections);
          initialExpandCompleteRef.current = true;
        }
        justEnteredTabRef.current = false;
        hasSyncedStoreToUrlRef.current = false;
        if (allMetricsSynced) {
          hasSyncedRef.current = true;
          lastSyncedParamsRef.current = currentParamsString;
          lastStationIdRef.current = currentStationId;
        }
        isSyncingRef.current = false; // Reset guard
        return;
      }
    }

    const currentMetricsLength =
      (investigationStore.devicesMetrics?.length || 0) +
      (investigationStore.stationMetrics?.length || 0) +
      (investigationStore.pressureDrops?.length || 0);

    const metricsWereRegenerated =
      hasSyncedRef.current && metricIdsFromUrl && !storeHasSelectedMetrics && currentMetricsLength > 0;

    if (metricsWereRegenerated) {
      hasSyncedRef.current = false;
      lastSyncedParamsRef.current = '';
    }

    const currentDevicesMetricsLength = investigationStore.devicesMetrics?.length || 0;
    const currentStationMetricsLength = investigationStore.stationMetrics?.length || 0;
    const devicesMetricsLengthChanged = lastDevicesMetricsLengthRef.current !== currentDevicesMetricsLength;
    const oldDevicesMetricsLength = lastDevicesMetricsLengthRef.current;

    const urlHasStationKpis = metricIdsFromUrl
      ?.split(',')
      .some(id => id.startsWith(`${EXPANDABLE_SECTION_KEYS.STATION_KPI}:`));
    const storeHasStationKpisSelected = investigationStore.stationMetrics?.some(m => m.isSelected) || false;
    const stationMetricsWereRegenerated =
      hasSyncedRef.current &&
      urlHasStationKpis &&
      !storeHasStationKpisSelected &&
      currentStationMetricsLength > 0 &&
      investigationStore.rootStore?.devicesStore?.isReady;

    const needsDeviceMetrics = metricIdsFromUrl
      ?.split(',')
      .some(
        id =>
          !id.startsWith(`${EXPANDABLE_SECTION_KEYS.STATION_KPI}:`) &&
          !id.startsWith(`${EXPANDABLE_SECTION_KEYS.PRESSURE_DROP}:`),
      );

    if (
      (needsDeviceMetrics &&
        devicesMetricsLengthChanged &&
        oldDevicesMetricsLength === 0 &&
        currentDevicesMetricsLength > 0 &&
        hasSyncedRef.current) ||
      stationMetricsWereRegenerated
    ) {
      hasSyncedRef.current = false;
      lastSyncedParamsRef.current = '';
    }

    lastMetricsLengthRef.current = currentMetricsLength;
    lastDevicesMetricsLengthRef.current = currentDevicesMetricsLength;
    lastStationMetricsLengthRef.current = currentStationMetricsLength;

    if (hasSyncedRef.current && lastSyncedParamsRef.current === currentParamsString) {
      isSyncingRef.current = false;
      return;
    }

    const shouldSkipMetricSync = doStoreMetricsMatchUrl(investigationStore, metricIdsFromUrl);
    const shouldSkipDateSync = doStoreDatesMatchUrl(investigationStore, searchParams);

    if (shouldSkipMetricSync && shouldSkipDateSync) {
      if (!initialExpandCompleteRef.current) {
        const newExpandState = getExpandStateFromStore(investigationStore);
        updateExpandStateIfChanged(
          newExpandState,
          devicesToExpand,
          expandableSectionsToExpand,
          setDevicesToExpand,
          setExpandableSectionsToExpand,
        );
        initialExpandCompleteRef.current = true;
      }

      hasSyncedRef.current = true;
      lastSyncedParamsRef.current = currentParamsString;
      lastStationIdRef.current = currentStationId;
      isSyncingRef.current = false;
      return;
    }

    if (shouldSkipMetricSync) {
      syncDatesFromUrl(investigationStore, searchParams);
      if (!initialExpandCompleteRef.current) {
        const newExpandState = getExpandStateFromStore(investigationStore);
        updateExpandStateIfChanged(
          newExpandState,
          devicesToExpand,
          expandableSectionsToExpand,
          setDevicesToExpand,
          setExpandableSectionsToExpand,
        );
        initialExpandCompleteRef.current = true;
      }

      hasSyncedRef.current = true;
      lastSyncedParamsRef.current = currentParamsString;
      lastStationIdRef.current = currentStationId;
      isSyncingRef.current = false;
      return;
    }

    try {
      const {
        devicesToExpand: devices,
        expandableSectionsToExpand: sections,
        allMetricsSynced,
      } = syncFromUrlToStore(investigationStore, searchParams);

      if (!initialExpandCompleteRef.current) {
        setDevicesToExpand(devices);
        setExpandableSectionsToExpand(sections);
        initialExpandCompleteRef.current = true;
      }

      if (allMetricsSynced) {
        hasSyncedRef.current = true;
        lastSyncedParamsRef.current = currentParamsString;
        lastStationIdRef.current = currentStationId;
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, [
    location.pathname,
    searchParams,
    investigationStore.isReady,
    investigationStore.rootStore?.devicesStore?.isReady,
    investigationStore.dates1?.[0]?.valueOf(),
    investigationStore.dates1?.[1]?.valueOf(),
    investigationStore.dates2?.[0]?.valueOf(),
    investigationStore.dates2?.[1]?.valueOf(),
    investigationStore.isCompareMode,
    stationStore?.currentStation?.id,
    investigationStore.devicesMetrics?.length || 0,
    setSearchParams,
  ]);

  const sectionKeysToExpand = useMemo(() => {
    const devicesReady = investigationStore.rootStore?.devicesStore?.isReady;
    const hasSortedDevices = !!investigationStore.sortedDevicesMetrics;

    if (!stationStore || !hasSortedDevices) {
      return undefined;
    }

    if (!devicesReady) {
      return undefined;
    }

    const visibleDevices = getVisibleDevices(investigationStore, stationStore);
    const keys: (string | number)[] = [];

    if (expandableSectionsToExpand.includes('station-total')) {
      keys.push(0);
    }

    const hasDeviceMetrics = devicesToExpand.length > 0;
    const devicesMetricsPopulated = (investigationStore.devicesMetrics?.length || 0) > 0;

    if (hasDeviceMetrics && devicesMetricsPopulated) {
      visibleDevices.forEach((device, index) => {
        if (devicesToExpand.includes(device.id)) {
          keys.push(index + 1);
        }
      });
    }

    const hasPressureDrops = expandableSectionsToExpand.includes('pressure-drop');
    if (hasPressureDrops && devicesMetricsPopulated) {
      const pressureDropIndex = 1 + visibleDevices.length;
      keys.push(pressureDropIndex);
    }

    return keys.length > 0 ? keys : undefined;
  }, [
    devicesToExpand,
    expandableSectionsToExpand,
    investigationStore,
    stationStore,
    investigationStore.rootStore?.devicesStore?.isReady,
    investigationStore.devicesMetrics?.length || 0,
  ]);

  return {
    sectionKeysToExpand,
  };
};
